import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import SeoHelmet from "../seo/SeoHelmet";
import { absoluteUrl, buildBreadcrumbSchema } from "../seo/schema";
import { blogCategories } from "../data/blogCategories";
import { blogPosts } from "../data/blogPosts";
import { getLatestPosts } from "./blogUtils";
import BlogCard from "./BlogCard";
import { useAuth } from "../auth";

const BLOG_QUERY_STORAGE_KEY = "cp_blog_keyword_insights_queries_v1";

// Admin-only feature toggle for keyword planning section on /blog.
const SHOW_KEYWORD_INSIGHTS_PANEL = true;

function BlogHome() {
  const { role } = useAuth();
  const isAdmin = role === "admin";
  const canViewKeywordInsights = SHOW_KEYWORD_INSIGHTS_PANEL && isAdmin;

  const [queryDraft, setQueryDraft] = useState("");

  const pillarCategories = useMemo(
    () => blogCategories.filter((category) => category.isPillar),
    []
  );

  const clusterCategories = useMemo(
    () => blogCategories.filter((category) => !category.isPillar),
    []
  );

  const latestPosts = useMemo(() => getLatestPosts(blogPosts, 6), []);

  useEffect(() => {
    if (!canViewKeywordInsights) return;
    try {
      setQueryDraft(window.localStorage.getItem(BLOG_QUERY_STORAGE_KEY) || "");
    } catch {
      setQueryDraft("");
    }
  }, [canViewKeywordInsights]);

  const handleSaveQueryNotes = () => {
    try {
      window.localStorage.setItem(BLOG_QUERY_STORAGE_KEY, queryDraft);
      window.alert("Keyword notes saved locally.");
    } catch {
      window.alert("Unable to save keyword notes in this browser.");
    }
  };

  return (
    <Container maxWidth="lg">
      <SeoHelmet
        title="Blog | Cyberpunk"
        description="Investigation-focused CDR and telecom analysis guides for Police, FIA, NCCIA, and NAB teams in Pakistan."
        canonical={absoluteUrl("/blog")}
        ogTitle="Cyberpunk Blog for CDR & Telecom Investigation"
        ogDescription="Pillar and cluster guides for lawful CDR analysis, IMEI attribution, location review, and evidence workflow."
        ogUrl={absoluteUrl("/blog")}
        schema={[
          buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
          ]),
        ]}
      />

      <Stack spacing={3.5}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
              Start Here for Investigators
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Use the pillar hub first, then move into category guides based on your case stage. The blog is designed
              for lawful investigation and compliance-focused workflows.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
              <Button
                component={RouterLink}
                to="/blog/category/cdr-investigation-analysis"
                variant="contained"
              >
                Open Pillar Hub
              </Button>
              <Button component={RouterLink} to="/cdr-analyzer" variant="outlined">
                CDR Analyzer Tool
              </Button>
              <Button component={RouterLink} to="/pattern-generator" variant="outlined">
                Pattern Generator
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Box>
          <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
            Pillar Categories
          </Typography>
          <Grid container spacing={2}>
            {pillarCategories.map((category) => (
              <Grid key={category.slug} item xs={12} md={6}>
                <Card
                  component={RouterLink}
                  to={`/blog/category/${category.slug}`}
                  variant="outlined"
                  sx={{
                    textDecoration: "none",
                    color: "inherit",
                    height: "100%",
                    "&:hover": { boxShadow: (theme) => theme.shadows[2] },
                  }}
                >
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Chip label="Pillar" size="small" color="primary" />
                    </Stack>
                    <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                      {category.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {category.intro}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box>
          <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
            Cluster Categories
          </Typography>
          <Grid container spacing={2}>
            {clusterCategories.map((category) => (
              <Grid key={category.slug} item xs={12} sm={6} md={3}>
                <Card
                  component={RouterLink}
                  to={`/blog/category/${category.slug}`}
                  variant="outlined"
                  sx={{
                    textDecoration: "none",
                    color: "inherit",
                    height: "100%",
                    "&:hover": { boxShadow: (theme) => theme.shadows[2] },
                  }}
                >
                  <CardContent>
                    <Typography variant="subtitle1" component="h3" sx={{ mb: 1 }}>
                      {category.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {category.intro}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box>
          <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
            Latest Posts
          </Typography>
          <Grid container spacing={2}>
            {latestPosts.map((post) => {
              const category = blogCategories.find((item) => item.slug === post.categorySlug);
              return (
                <Grid key={post.id} item xs={12} md={6}>
                  <BlogCard post={post} categoryName={category?.name} />
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {canViewKeywordInsights ? (
          <Card variant="outlined" sx={{ borderColor: "primary.main" }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Keyword Insights (Admin)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Connect Google Search Console and use this notes area to track high-intent investigator queries.
              </Typography>

              {/*
                GSC workflow for editors:
                1) Search Console -> Performance -> Search results.
                2) Filter pages by /blog and export top queries + clicks + impressions.
                3) Prioritize keywords with high impressions and low CTR, then draft next cluster posts.
              */}
              <TextField
                label="Top Queries Notes"
                placeholder="Example: 'cdr analysis pakistan', 'imei from cdr', 'cell id investigation'"
                multiline
                minRows={5}
                fullWidth
                value={queryDraft}
                onChange={(event) => setQueryDraft(event.target.value)}
              />
              <Stack direction="row" spacing={1.25} sx={{ mt: 1.5 }}>
                <Button variant="contained" onClick={handleSaveQueryNotes}>
                  Save Notes
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setQueryDraft("");
                    try {
                      window.localStorage.removeItem(BLOG_QUERY_STORAGE_KEY);
                    } catch {
                      // ignore storage failure
                    }
                  }}
                >
                  Clear
                </Button>
              </Stack>
            </CardContent>
          </Card>
        ) : null}

        <Divider />
        <Typography variant="caption" color="text.secondary" sx={{ pb: 1 }}>
          For lawful investigation and compliance only.
        </Typography>
      </Stack>
    </Container>
  );
}

export default BlogHome;
